import * as THREE from 'three';

export interface UVBounds {
    minU: number;
    maxU: number;
    minV: number;
    maxV: number;
    width: number;
    height: number;
    aspectRatio: number;
    needsFlipY: boolean;
}

export const analyzeUVs = (geometry: THREE.BufferGeometry): UVBounds | null => {
    const uvAttribute = geometry.getAttribute('uv');
    if (!uvAttribute) return null;

    let minU = Infinity, maxU = -Infinity;
    let minV = Infinity, maxV = -Infinity;

    for (let i = 0; i < uvAttribute.count; i++) {
        const u = uvAttribute.getX(i);
        const v = uvAttribute.getY(i);
        
        minU = Math.min(minU, u);
        maxU = Math.max(maxU, u);
        minV = Math.min(minV, v);
        maxV = Math.max(maxV, v);
    }

    const width = maxU - minU;
    const height = maxV - minV;
    
    // Auto-detect UV orientation:
    // Standard UV: V=0 at bottom, V=1 at top (OpenGL convention)
    // If center of UVs is in upper half OR if UVs span 0-1 with center > 0.5, likely needs flip
    const centerV = (minV + maxV) / 2;
    
    // Detect if the print area's UV space is inverted
    // If the UV center is above 0.5 and close to 1, or if minV is close to 1, it's inverted
    const needsFlipY = (maxV > 0.9 && minV > 0.4) || // UVs clustered near top
                       (centerV > 0.6 && height < 0.5); // Small print area in upper region

    return {
        minU, maxU, minV, maxV,
        width,
        height,
        aspectRatio: width / height,
        needsFlipY
    };
};

// Consolidated diagnostic function matching ModelLoader usage
export const diagnoseUVMapping = (mesh: THREE.Mesh | THREE.BufferGeometry) => {
    const geometry = mesh instanceof THREE.Mesh ? mesh.geometry : mesh;
    const bounds = analyzeUVs(geometry);
    
    if (!bounds) {
        return { hasUVs: false, uvRange: null, needsFlipY: false };
    }

    return {
        hasUVs: true,
        uvRange: bounds,
        needsFlipY: bounds.needsFlipY
    };
};

/**
 * Box Projection UVs for square/rectangular geometry
 * Assigns each face 1/4 of the U range based on face normal direction
 * @param geometry - The buffer geometry to modify
 * @param axis - The axis along which the object extends ('z' for pen length)
 * @param uOffset - Offset to rotate the texture alignment
 */
export const applyBoxProjectionUVs = (geometry: THREE.BufferGeometry, axis: 'x' | 'y' | 'z' = 'z', uOffset: number = 0.0) => {
    const posAttribute = geometry.getAttribute('position');
    const normalAttribute = geometry.getAttribute('normal');
    const uvAttribute = geometry.getAttribute('uv');
    
    if (!normalAttribute) {
        geometry.computeVertexNormals();
    }
    const normals = geometry.getAttribute('normal');
    
    geometry.computeBoundingBox();
    const bbox = geometry.boundingBox!;
    
    // Determine length axis range for V mapping
    let lengthMin = 0, lengthMax = 0;
    if (axis === 'z') {
        lengthMin = bbox.min.z;
        lengthMax = bbox.max.z;
    } else if (axis === 'x') {
        lengthMin = bbox.min.x;
        lengthMax = bbox.max.x;
    } else {
        lengthMin = bbox.min.y;
        lengthMax = bbox.max.y;
    }
    const lengthRange = lengthMax - lengthMin;
    
    // For each vertex, determine which face it belongs to based on normal
    for (let i = 0; i < posAttribute.count; i++) {
        const nx = normals.getX(i);
        const ny = normals.getY(i);
        const nz = normals.getZ(i);
        
        const x = posAttribute.getX(i);
        const y = posAttribute.getY(i);
        const z = posAttribute.getZ(i);
        
        // Get components perpendicular to the main axis
        let n1 = 0, n2 = 0, p1 = 0, p2 = 0, lengthPos = 0;
        let range1 = 0, range2 = 0, min1 = 0, min2 = 0;
        
        if (axis === 'z') {
            n1 = nx; n2 = ny; p1 = x; p2 = y; lengthPos = z;
            range1 = bbox.max.x - bbox.min.x;
            range2 = bbox.max.y - bbox.min.y;
            min1 = bbox.min.x; min2 = bbox.min.y;
        } else if (axis === 'x') {
            n1 = ny; n2 = nz; p1 = y; p2 = z; lengthPos = x;
            range1 = bbox.max.y - bbox.min.y;
            range2 = bbox.max.z - bbox.min.z;
            min1 = bbox.min.y; min2 = bbox.min.z;
        } else {
            n1 = nx; n2 = nz; p1 = x; p2 = z; lengthPos = y;
            range1 = bbox.max.x - bbox.min.x;
            range2 = bbox.max.z - bbox.min.z;
            min1 = bbox.min.x; min2 = bbox.min.z;
        }
        
        // Determine which face based on dominant normal direction
        // Order: Face3 → Face1 → Face4 → Face2 (Face1 in center of 2D canvas)
        let faceIndex = 0;
        let localU = 0;
        
        if (Math.abs(n1) > Math.abs(n2)) {
            // Normal points mainly in n1 direction
            if (n1 > 0) {
                faceIndex = 1; // Face 1: +n1 direction (CENTER of canvas)
                localU = (p2 - min2) / range2;
            } else {
                faceIndex = 3; // Face 2: -n1 direction (opposite to Face 1)
                localU = 1 - (p2 - min2) / range2;
            }
        } else {
            // Normal points mainly in n2 direction
            if (n2 > 0) {
                faceIndex = 2; // Face 3: +n2 direction
                localU = 1 - (p1 - min1) / range1; // Reversed direction
            } else {
                faceIndex = 0; // Face 4: -n2 direction
                localU = (p1 - min1) / range1; // Reversed direction
            }
        }
        
        // CLAMP localU to 0-1
        localU = Math.max(0, Math.min(1, localU));

        // EXPAND localU to overlap adjacent faces
        // Overlap of 4% local (1% global) ensures no gaps
        const overlap = 0.04;
        const expandedLocalU = localU * (1 + 2 * overlap) - overlap;
        
        // Map to global 0.25 slot
        let u = (faceIndex * 0.25) + (expandedLocalU * 0.25) + uOffset;

        // NOTE: We deliberately do NOT wrap u to [0,1].
        // Rely on RepeatWrapping to handle <0 and >1 values for seamless join.
        
        // V is based on position along length axis
        const v = 1.0 - ((lengthPos - lengthMin) / lengthRange);
        
        uvAttribute.setXY(i, u, v);
    }
    
    // Debug: Log face UV ranges
    const faceRanges: { [key: number]: { minU: number, maxU: number } } = {};
    for (let i = 0; i < posAttribute.count; i++) {
        const u = uvAttribute.getX(i);
        const nx = normals.getX(i);
        const ny = normals.getY(i);
        
        let n1 = 0, n2 = 0;
        if (axis === 'z') { n1 = nx; n2 = ny; }
        else if (axis === 'x') { n1 = ny; n2 = normals.getZ(i); }
        else { n1 = nx; n2 = normals.getZ(i); }
        
        let faceLabel = '';
        if (Math.abs(n1) > Math.abs(n2)) {
            faceLabel = n1 > 0 ? 'Face1' : 'Face2';
        } else {
            faceLabel = n2 > 0 ? 'Face3' : 'Face4';
        }
        
        if (!faceRanges[faceLabel as any]) {
            faceRanges[faceLabel as any] = { minU: u, maxU: u };
        } else {
            faceRanges[faceLabel as any].minU = Math.min(faceRanges[faceLabel as any].minU, u);
            faceRanges[faceLabel as any].maxU = Math.max(faceRanges[faceLabel as any].maxU, u);
        }
    }

    
    uvAttribute.needsUpdate = true;
};

export const applyCylindricalUVs = (geometry: THREE.BufferGeometry, axis: 'x' | 'y' | 'z' = 'y', uOffset: number = 0.0, forceFullCircle: boolean = false) => {
    const posAttribute = geometry.getAttribute('position');
    const uvAttribute = geometry.getAttribute('uv');
    const count = posAttribute.count;

    geometry.computeBoundingBox();
    const bbox = geometry.boundingBox!;

    let rangeMin = 0;
    let rangeLen = 0;

    if (axis === 'y') {
        rangeMin = bbox.min.y;
        rangeLen = bbox.max.y - bbox.min.y;
    } else if (axis === 'x') {
        rangeMin = bbox.min.x;
        rangeLen = bbox.max.x - bbox.min.x;
    } else if (axis === 'z') {
        rangeMin = bbox.min.z;
        rangeLen = bbox.max.z - bbox.min.z;
    }
    
    // Calculate angles for all vertices relative to CENTER
    const angles = new Float32Array(count);
    const centerX = (bbox.min.x + bbox.max.x) / 2;
    const centerY = (bbox.min.y + bbox.max.y) / 2;
    const centerZ = (bbox.min.z + bbox.max.z) / 2;

    for (let i = 0; i < count; i++) {
        const x = posAttribute.getX(i);
        const y = posAttribute.getY(i);
        const z = posAttribute.getZ(i);
        
        let angle = 0;
        // atan2(y, x) -> result is angle in radians
        if (axis === 'y') {
            angle = Math.atan2(x - centerX, z - centerZ);
        } else if (axis === 'x') {
            angle = Math.atan2(y - centerY, z - centerZ);
        } else if (axis === 'z') {
            angle = Math.atan2(y - centerY, x - centerX);
        }

        // Normalize to [0, 2π]
        if (angle < 0) angle += Math.PI * 2;
        angles[i] = angle;
    }

    // Determine range
    let minAngle = 0;
    let range = Math.PI * 2; // Full circle
    
    if (!forceFullCircle) {
        // Auto-range based on actual vertex angles
        minAngle = Infinity;
        let maxAngle = -Infinity;
        for(let i=0; i<count; i++) {
             if (angles[i] < minAngle) minAngle = angles[i];
             if (angles[i] > maxAngle) maxAngle = angles[i];
        }
        range = maxAngle - minAngle;
        if (range < 0.0001) range = Math.PI * 2; // Fallback
    }
    
    // Apply UVs
    for (let i = 0; i < count; i++) {
        let u = 0.5;
        if (range > 0.0001) {
            u = ((angles[i] - minAngle) / range) + uOffset;
            // Wrap U
            while (u > 1.0) u -= 1.0;
            while (u < 0.0) u += 1.0;
        }

        // Map height to V [0, 1]
        let pos = 0;
        if (axis === 'y') pos = posAttribute.getY(i);
        else if (axis === 'x') pos = posAttribute.getX(i);
        else if (axis === 'z') pos = posAttribute.getZ(i);
        
        const v = 1.0 - ((pos - rangeMin) / rangeLen);

        uvAttribute.setXY(i, u, v);
    }

    uvAttribute.needsUpdate = true;

};

export const normalizeUVs = (geometry: THREE.BufferGeometry) => {
    const uvAttribute = geometry.getAttribute('uv');
    if (!uvAttribute) return;

    const count = uvAttribute.count;
    let minU = Infinity, maxU = -Infinity;
    let minV = Infinity, maxV = -Infinity;

    // Find bounds
    for (let i = 0; i < count; i++) {
        const u = uvAttribute.getX(i);
        const v = uvAttribute.getY(i);
        minU = Math.min(minU, u);
        maxU = Math.max(maxU, u);
        minV = Math.min(minV, v);
        maxV = Math.max(maxV, v);
    }

    const width = maxU - minU;
    const height = maxV - minV;

    // Avoid division by zero
    if (width < 0.0001 || height < 0.0001) return;

    // Remap to 0-1
    for (let i = 0; i < count; i++) {
        const u = uvAttribute.getX(i);
        const v = uvAttribute.getY(i);
        
        const newU = (u - minU) / width;
        const newV = (v - minV) / height;
        
        uvAttribute.setXY(i, newU, newV);
    }

    uvAttribute.needsUpdate = true;
};
